/**
 * React Admin AuthProvider.
 *
 * Failure-path policy:
 *
 *   - **401** from the API means the token is no longer accepted (expired,
 *     revoked, signature broken). Try one refresh; if that fails too, send
 *     the user back through `sso.login()` so they re-enter Keycloak's flow
 *     and come back to the same page authenticated. We do NOT redirect to
 *     the corporate portal — the portal uses a different OIDC client and
 *     dropping the user there strands them outside the app.
 *
 *   - **403** from the API means the user is authenticated but their role
 *     lacks permission for the endpoint. This is NOT an auth failure;
 *     logging them out would loop them through Keycloak only to land back
 *     with the same role and get 403 again. We let the error bubble up so
 *     React Admin shows the standard error UI; the realm admin still needs
 *     to assign the missing role on the `dev-notify` client.
 *
 *   - **User-initiated logout** is the only path that goes to the portal.
 */

import { AuthProvider } from 'react-admin';
import sso from '@/auth/sso';
import { syncAuthToLocalStorage, clearAuthData } from '@/auth/authSync';
import type { AppIdentity } from '@/types/api';

const PORTAL_URL =
  import.meta.env.VITE_PORTAL_URL ?? 'https://portal.roommatik.com';
const POST_LOGIN_REDIRECT = '/apps/accounts';

const reauthThroughKeycloak = (): Promise<never> => {
  clearAuthData();
  sso.login({
    redirectUri: `${window.location.origin}${POST_LOGIN_REDIRECT}`,
  });
  // The call above navigates the browser. Return a never-resolving promise
  // so react-admin doesn't race ahead and render anything.
  return new Promise<never>(() => {});
};

export const authProvider: AuthProvider = {
  login: async () => Promise.resolve(),

  logout: async (): Promise<void> => {
    clearAuthData();
    // Drop the persisted theme preference so the next login starts in
    // light mode regardless of what the user picked last session.
    localStorage.removeItem("RaStore.theme");
    if (sso.authenticated) {
      sso.logout({ redirectUri: PORTAL_URL });
    } else {
      window.location.href = PORTAL_URL;
    }
    return new Promise<void>(() => {});
  },

  checkAuth: async (): Promise<void> => {
    if (!sso.authenticated) {
      await reauthThroughKeycloak();
      return;
    }
    try {
      const refreshed = await sso.updateToken(30);
      if (refreshed) syncAuthToLocalStorage(sso);
    } catch {
      await reauthThroughKeycloak();
    }
  },

  checkError: async (error): Promise<void> => {
    const status = error?.status;

    // 401 — token rejected. Attempt one refresh; if refresh fails, send the
    // user through Keycloak (which will use the SSO cookie silently if it's
    // still alive).
    if (status === 401) {
      try {
        const refreshed = await sso.updateToken(30);
        if (refreshed) syncAuthToLocalStorage(sso);
      } catch {
        await reauthThroughKeycloak();
        return Promise.reject({ redirectTo: false });
      }
      return Promise.resolve();
    }

    // 403 — authenticated but lacks role. Let react-admin show the standard
    // "Forbidden" notification. The realm admin needs to assign the missing
    // Notify client role on `dev-notify` to fix this.
    return Promise.resolve();
  },

  getIdentity: async (): Promise<AppIdentity> => {
    const parsed = sso.tokenParsed as Record<string, string> | undefined;
    return {
      id: parsed?.sub ?? '',
      fullName: parsed?.name ?? parsed?.preferred_username ?? '',
      email: parsed?.email ?? '',
      role: localStorage.getItem('role') ?? '',
    };
  },

  getPermissions: async (): Promise<string> =>
    localStorage.getItem('role') ?? '',
};

export default authProvider;
